import { useState, useEffect, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { clearToken } from '@/lib/queryClient';
import { queryClient } from '@/lib/queryClient';

const SESSION_REFRESH_INTERVAL = 3 * 60 * 60 * 1000; // 3 hours in milliseconds
const SESSION_START_KEY = 'sessionStartTime';

interface SessionRefreshModalProps {
  isAuthenticated: boolean;
}

export function SessionRefreshModal({ isAuthenticated }: SessionRefreshModalProps) {
  const [showModal, setShowModal] = useState(false);

  const handleRefresh = useCallback(() => {
    localStorage.removeItem(SESSION_START_KEY);
    clearToken();
    queryClient.clear();
    window.location.replace('/login');
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem(SESSION_START_KEY);
      setShowModal(false);
      return;
    }

    const storedStartTime = localStorage.getItem(SESSION_START_KEY);
    let sessionStartTime: number;

    if (storedStartTime) {
      sessionStartTime = parseInt(storedStartTime, 10);
    } else {
      sessionStartTime = Date.now();
      localStorage.setItem(SESSION_START_KEY, sessionStartTime.toString());
    }

    const checkSession = () => {
      const elapsed = Date.now() - sessionStartTime;
      if (elapsed >= SESSION_REFRESH_INTERVAL) {
        setShowModal(true);
      }
    };

    checkSession();

    const intervalId = setInterval(checkSession, 60000); // Check every minute

    return () => {
      clearInterval(intervalId);
    };
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AlertDialog open={showModal} onOpenChange={() => {}}>
      <AlertDialogContent className="sm:max-w-[425px]">
        <AlertDialogHeader>
          <AlertDialogTitle>Session Refresh Required</AlertDialogTitle>
          <AlertDialogDescription>
            Your session has been active for 3 hours. For security purposes, please refresh your session by logging in again.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleRefresh}>
            Refresh Session
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
